import { api } from "./client.js";
import { parseApplicationFields, parseApplicationAnswers } from "@wwrp/shared";
import type {
  Application,
  ApplicationTemplate,
  ApplicationAnswers,
  ApplicationType,
  ApplicationHistoryItem,
  ApplicationActivityItem,
} from "@wwrp/shared";

export type ApplicationDetail = Application & {
  activity?: ApplicationActivityItem[];
  applicantWebsiteId?: string | null;
  decisionUnavailableReasons?: Partial<
    Record<"source_department" | "target_department", string | null>
  >;
  staffNotes?: { id: number; body: string; createdAt: string }[];
  actions: Record<string, boolean>;
};

// Also accept JSON text from older API deployments during rollout.
function normalizeApplication<T extends Application>(application: T): T {
  return {
    ...application,
    fields: parseApplicationFields(application.fields),
    answers: parseApplicationAnswers(application.answers),
  };
}

export const applicationsApi = {
  history: (memberId: number) =>
    api.get<ApplicationHistoryItem[]>(`/members/${memberId}/applications`),
  templates: async () => {
    const applications = await api.get<ApplicationTemplate[]>("/applications/templates");
    return applications.map((application) => ({
      ...application,
      fields: parseApplicationFields(application.fields),
    }));
  },
  list: async () => (await api.get<Application[]>("/applications")).map(normalizeApplication),
  detail: async (id: number) =>
    normalizeApplication(await api.get<ApplicationDetail>(`/applications/${id}`)),
  submit: (templateId: number, answers: ApplicationAnswers, type?: ApplicationType) =>
    api.post<{ id: number }>("/applications", { templateId, answers, type }),
  decision: (
    id: number,
    body: { side: string; round: number; decision: string; note?: string; stipulations?: string },
  ) => api.post(`/applications/${id}/reviews`, body),
  message: (id: number, body: string) => api.post(`/applications/${id}/messages`, { body }),
  privateNote: (id: number, body: string) => api.post(`/applications/${id}/notes`, { body }),
  withdraw: (id: number) => api.post(`/applications/${id}/withdraw`),
  createTemplate: (body: unknown) => api.post<{ id: number }>("/applications/templates", body),
  updateTemplate: (id: number, body: unknown) => api.patch(`/applications/templates/${id}`, body),
};
